import { Injectable } from '@angular/core';
import {
  ICellChange,
  ICellChangeResponse, IQuotationCost, IQuotationsQuotation, IQuotationTraveller, IQuotationVatConfiguration
} from "../../../../../libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { EVatDisplayMode } from "../../../../../libraries/service/vat/vat.interface";
import {
  IQuotationQuotationCategoriesMarginPut,
  IQuotationQuotationsBlockPatch
} from "../../../../../libraries/service/resources/quotation-quotations/quotation-quotations-resource.service";
import { CachedQuotationService } from "../../../../shared/services/cached-quotation.service";
import { EQuotationEntryPoints } from "libraries/enums/e-quotation-entry-points";

@Injectable({
  providedIn: 'root'
})
export class CellActionUpdateService {
  public quotation: IQuotationsQuotation;
  public quotationVatConfiguration: IQuotationVatConfiguration;
  public subPage: EQuotationEntryPoints;

  constructor(
      private readonly cachedQuotationService: CachedQuotationService,) {
  }

  public setInitValues(quotation: IQuotationsQuotation, quotationVatConfiguration: IQuotationVatConfiguration, subPage: EQuotationEntryPoints): void{
    this.quotation = quotation;
    this.quotationVatConfiguration = quotationVatConfiguration;
    this.subPage = subPage;
  }

  public updateQuotationProfit(cellChange: ICellChange): Observable<ICellChangeResponse> {
    return this.cachedQuotationService
        .patch(
            {
              retail: {
                profit: {
                  value: cellChange.output.price.value
                }
              }
            },
            this.quotation.id
        )
        .pipe(
            map((quotation: IQuotationsQuotation): ICellChangeResponse => ({ quotation, itemType: cellChange.output.itemType }))
        );
  }

  public updateQuotationTotalRetail(cellChange: ICellChange): Observable<ICellChangeResponse> {
    const request: object = (this.quotationVatConfiguration.vatDisplayMode === EVatDisplayMode.includingVAT) ?
        { retailIncludingVat: { value: cellChange.output.price.value, } } :
        { retail: { value: cellChange.output.price.value, } };

    return this.cachedQuotationService
        .patch(
            request,
            this.quotation.id
        )
        .pipe(
            map((quotation: IQuotationsQuotation): ICellChangeResponse => ({ quotation, itemType: cellChange.output.itemType }))
        );
  }

  public updateQuotationMargin(cellChange: ICellChange): Observable<ICellChangeResponse> {

    const request: IQuotationQuotationCategoriesMarginPut = {
      categoryRates: cellChange.output.marginRequest,
      optionBlocksOnly: this.subPage === EQuotationEntryPoints.option
    };
    return this.cachedQuotationService
        .putQuotationQuotationsCategories(
            this.quotation.id,
            cellChange.output.calculationMode,
            request
        )
        .pipe(
            map((quotation: IQuotationsQuotation): ICellChangeResponse => ({ quotation, itemType: cellChange.output.itemType }))
        );
  }

  public updateQuotationCommission(cellChange: ICellChange): Observable<ICellChangeResponse> {

    const request: IQuotationQuotationCategoriesMarginPut = {
      categoryRates: cellChange.output.commissionRequest,
      optionBlocksOnly: this.subPage === EQuotationEntryPoints.option
    };
    return this.cachedQuotationService
        .putQuotationQuotationsCategories(
            this.quotation.id,
            cellChange.output.calculationMode,
            request
        )
        .pipe(
            map((quotation: IQuotationsQuotation):ICellChangeResponse => ({ quotation, itemType: cellChange.output.itemType }))
        );
  }

  public updateBlockMargin(cellChange: ICellChange): Observable<ICellChangeResponse> {
    return this.cachedQuotationService
        .patchBlock(
            this.quotation.id,
            {
              retail: {
                margin: {
                  used: cellChange.output.retail.margin.used,
                  reference: cellChange.output.retail.margin.reference,
                }
              },
            },
            cellChange.output.blockId
        )
        .pipe(
            map((quotation: IQuotationsQuotation): ICellChangeResponse => (
                {
                  quotation,
                  itemType: cellChange.output.itemType,
                  blockId: cellChange.output.blockId
                }))
        );
  }

  public updateBlockMarkup(cellChange: ICellChange): Observable<ICellChangeResponse> {
    return this.cachedQuotationService
        .patchBlock(
            this.quotation.id,
            {
              retail: {
                markup: {
                  used: cellChange.output.retail.markup.used,
                  reference: cellChange.output.retail.markup.reference,
                }
              },
            },
            cellChange.output.blockId
        )
        .pipe(
            map((quotation: IQuotationsQuotation): ICellChangeResponse => (
                {
                  quotation,
                  itemType: cellChange.output.itemType,
                  blockId: cellChange.output.blockId
                }))
        );
  }

  public updateBlockProfit(cellChange: ICellChange): Observable<ICellChangeResponse> {
    return this.cachedQuotationService
        .patchBlock(
            this.quotation.id,
            {
              retail: {
                profit: {
                  value: cellChange.output.price.value,
                }
              },
            },
            cellChange.output.blockId
        )
        .pipe(
            map((quotation: IQuotationsQuotation): ICellChangeResponse => (
                {
                  quotation,
                  itemType: cellChange.output.itemType,
                  blockId: cellChange.output.blockId
                }))
        );
  }

  public updateBlockRetail(cellChange: ICellChange): Observable<ICellChangeResponse> {

    const request: IQuotationQuotationsBlockPatch = (this.quotationVatConfiguration.vatDisplayMode === EVatDisplayMode.includingVAT) ?
        { retailIncludingVat: { value: cellChange.output.retail.value } } :
        { retail: { value: cellChange.output.retail.value } };

    return this.cachedQuotationService
        .patchBlock(
            this.quotation.id,
            request,
            cellChange.output.blockId
        )
        .pipe(
            map((quotation: IQuotationsQuotation): ICellChangeResponse => (
                {
                  quotation,
                  itemType: cellChange.output.itemType,
                  blockId: cellChange.output.blockId
                }))
        );
  }

  public updateQuotationAdjustment(cellChange: ICellChange): Observable<ICellChangeResponse> {
    const request: object = (this.quotationVatConfiguration.vatDisplayMode === EVatDisplayMode.includingVAT) ?
        { retailIncludingVat: { adjustment: { value: cellChange.output.price.value } }, } :
        { retail: { adjustment: { value: cellChange.output.price.value } }, };
    return this.cachedQuotationService
        .patch(
            request,
            this.quotation.id
        )
        .pipe(
            map((quotation: IQuotationsQuotation): ICellChangeResponse => ({ quotation, itemType: cellChange.output.itemType }))
        );
  }

  public updateQuotationVatConfiguration(cellChange: ICellChange): Observable<ICellChangeResponse> {
    return this.cachedQuotationService
        .patch(
            {
              vat: {
                rate: cellChange.output.vatConfiguration.vatRate,
              },
              retail: {
                displayMode: cellChange.output.vatConfiguration.vatDisplayMode,
              },
            },
            this.quotation.id
        )
        .pipe(
            map((quotation: IQuotationsQuotation): ICellChangeResponse => ({ quotation, itemType: cellChange.output.itemType }))
        );
  }

  public updateTravellerTotalRetail(cellChange: ICellChange): Observable<ICellChangeResponse> {
    const retail: IQuotationCost = {
      value: cellChange.output.retail.value,
      /*temporary fix, waiting for API*/
      currency: cellChange.output.retail.currency || "EUR"
    };

    const request: IQuotationTraveller = (this.quotationVatConfiguration.vatDisplayMode === EVatDisplayMode.includingVAT) ?
        { retailIncludingVat: retail, } :
        { retail };

    return this.cachedQuotationService
        .patchTraveller(
            request,
            cellChange.output.travellerId
        )
        .pipe(
            map((quotation: IQuotationsQuotation): ICellChangeResponse => (
                {
                  quotation,
                  itemType: cellChange.output.itemType,
                  travellerId: cellChange.output.travellerId,
                }
            )),
        );
  }

  public updateTravellerAdjustment(cellChange: ICellChange): Observable<ICellChangeResponse> {

    const request: object = (this.quotationVatConfiguration.vatDisplayMode === EVatDisplayMode.includingVAT) ?
        { retailIncludingVat: { adjustment: { value: cellChange.output.price.value } }, } :
        { retail: { adjustment: { value: cellChange.output.price.value } }, };

    return this.cachedQuotationService
        .patchTraveller(
            request,
            cellChange.output.travellerId
        )
        .pipe(
            map((quotation: IQuotationsQuotation): ICellChangeResponse => (
                {
                  quotation,
                  itemType: cellChange.output.itemType,
                  travellerId: cellChange.output.travellerId,
                }
            )),
        );
  }

}
